import { Box, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useOperatorsRidesVehicleCandidatesQuery } from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { RideDtoSchemaType, UserPermission, colors } from 'remote/global';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BusIcon } from 'utils/custom-icons';
import { VehicleCandidate } from 'utils/data/data-types';
import { getModifiedCandidatesArray } from 'utils/helpers';

type SelectVehicleProps = {
  selectedRide?: RideDtoSchemaType;
  selectedVehicleCandidate?: VehicleCandidate;
  setVehicleCandidate: (candidate: VehicleCandidate) => void;
  isEditDisabled: boolean;
};

const getIsSelectDisabled = (permissions: any) => {
  switch (permissions?.ORDER) {
    case UserPermission.NO_ACCESS:
      return true;
    case UserPermission.RESTRICTED:
      return true;
    case UserPermission.READ_OWN:
      return true;
    case UserPermission.READ_ALL:
      return true;
    default:
      return false;
  }
};

function SelectVehicle({
  selectedRide,
  setVehicleCandidate,
  selectedVehicleCandidate,
  isEditDisabled,
}: SelectVehicleProps) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const isSelectDisabled = isEditDisabled || getIsSelectDisabled(permissions);
  const [isVehicleCandidatesQueryEnabled, setIsVehicleCandidatesQueryEnabled] = useState(false);
  const { data: vehicleCandidates } = useOperatorsRidesVehicleCandidatesQuery(
    String(selectedRide?.id),
    isVehicleCandidatesQueryEnabled
  );

  const modifiedArray = getModifiedCandidatesArray(vehicleCandidates ?? [], {
    value: selectedRide?.vehicle?.licensePlate || '',
    id: selectedRide?.vehicle.id || 0,
  });

  const currentSelectedVehicle: VehicleCandidate = useMemo(() => {
    if (selectedVehicleCandidate != null) return selectedVehicleCandidate;

    if (selectedRide?.vehicle != null) {
      return {
        id: selectedRide.vehicle.id,
        value: selectedRide.vehicle.licensePlate,
      };
    }

    // Displays the placeholder
    return {
      id: '' as any,
      value: '',
    };
  }, [selectedVehicleCandidate, selectedRide?.vehicle]);

  const handleToggleQuery = () => {
    // Hack to trigger enabled from false to true
    setIsVehicleCandidatesQueryEnabled(false);
    setTimeout(() => {
      setIsVehicleCandidatesQueryEnabled(true);
    }, 1);
  };

  const handleSelectVehicle = (e: SelectChangeEvent<number>) => {
    const value = Number(e.target.value);
    const driver = vehicleCandidates?.find((v) => v.id === value);
    if (driver != null) setVehicleCandidate(driver);
  };

  return (
    <Box>
      <InputLabel
        id='vehicle-label'
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: isSelectDisabled ? colors.gray3 : '#344054',
          marginBottom: '3px',
        }}
      >
        {t('FormFields.vehicleId.label')}
      </InputLabel>
      <Select
        labelId='vehicle-label'
        disabled={isSelectDisabled}
        fullWidth
        value={currentSelectedVehicle.id}
        onFocus={handleToggleQuery}
        renderValue={(candidateId) => {
          let candidate = vehicleCandidates?.find((v) => v.id === candidateId);
          if (candidate == null && currentSelectedVehicle != null)
            candidate = currentSelectedVehicle;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
              <BusIcon {...(isSelectDisabled && { stroke: colors.gray3 })} />
              {candidate?.value || t('FormFields.vehicleId.placeholder')}
            </Box>
          );
        }}
        displayEmpty
        onChange={handleSelectVehicle}
      >
        {modifiedArray.map((candidate) => {
          return (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.value}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
}

export default SelectVehicle;
